// app/create-account/page.tsx
"use client";

import { useState } from "react";

type Role = "Parent" | "Coach";

export default function CreateAccountPage() {
  const [form, setForm] = useState({
    role: "" as "" | Role,

    name: "",
    email: "",
    phone: "",

    password: "",
    confirm: "",

    // Coach fields
    teamName: "",
    coachDisplayName: "",
    contactEmail: "",
    city: "",
    state: "",
  });

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    setErr(null);
    setMsg(null);

    if (!form.role) {
      return setErr("Please select Parent or Coach.");
    }

    if (form.password !== form.confirm) {
      return setErr("Passwords do not match.");
    }

    if (form.password.length < 8) {
      return setErr("Password must be at least 8 characters.");
    }

    setLoading(true);

    try {
      const payload = {
        role: form.role,

        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,

        password: form.password,

        // Coach-only fields
        teamName: form.role === "Coach" ? form.teamName.trim() : "",
        coachDisplayName:
          form.role === "Coach" ? form.coachDisplayName.trim() : "",

        // 🔥 FIX: Parent auto-uses their email
        contactEmail:
          form.role === "Parent"
            ? form.email.trim()
            : form.contactEmail.trim(),

        city: form.role === "Coach" ? form.city.trim() : "",
        state: form.role === "Coach" ? form.state.trim() : "",
      };

      const res = await fetch("/api/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        const fieldErrs =
          data?.errors &&
          Object.entries<string[]>(data.errors)
            .map(([k, v]) => `${k}: ${v.join(", ")}`)
            .join(" | ");

        setErr(data?.message || fieldErrs || "Registration failed");
        return;
      }

      setMsg("Account created! Redirecting to login…");

      setTimeout(() => {
        window.location.href = `/login?email=${encodeURIComponent(
          form.email.trim()
        )}`;
      }, 800);
    } catch (ex: any) {
      setErr(ex?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="rr-container">
      <h1 className="mb-4 text-2xl font-semibold">
        Create Account
      </h1>

      <form onSubmit={handleSubmit} className="rr-card space-y-5">
        {/* Role */}
        <fieldset>
          <legend className="rr-label mb-2">I am a…</legend>

          <div className="flex gap-6">
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="role"
                value="Parent"
                checked={form.role === "Parent"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    role: e.target.value as Role,
                  }))
                }
                className="h-4 w-4"
                required
              />
              <span>Parent</span>
            </label>

            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="role"
                value="Coach"
                checked={form.role === "Coach"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    role: e.target.value as Role,
                  }))
                }
                className="h-4 w-4"
                required
              />
              <span>Coach</span>
            </label>
          </div>
        </fieldset>

        {/* Name */}
        <label className="block">
          <span className="rr-label">Name</span>
          <input
            className="rr-input"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            required
          />
        </label>

        {/* Email */}
        <label className="block">
          <span className="rr-label">Email</span>
          <input
            type="email"
            className="rr-input"
            value={form.email}
            onChange={(e) =>
              setForm({ ...form, email: e.target.value })
            }
            required
          />
        </label>

        {/* Phone */}
        <label className="block">
          <span className="rr-label">Phone (optional)</span>
          <input
            type="tel"
            className="rr-input"
            value={form.phone}
            onChange={(e) =>
              setForm({ ...form, phone: e.target.value })
            }
            placeholder="e.g. 555-555-5555"
          />
        </label>

        {/* Coach Fields */}
        {form.role === "Coach" && (
          <>
            <label className="block">
              <span className="rr-label">Team Name</span>
              <input
                className="rr-input"
                value={form.teamName}
                onChange={(e) =>
                  setForm({ ...form, teamName: e.target.value })
                }
                required
              />
            </label>

            <label className="block">
              <span className="rr-label">Coach Display Name</span>
              <input
                className="rr-input"
                value={form.coachDisplayName}
                onChange={(e) =>
                  setForm({
                    ...form,
                    coachDisplayName: e.target.value,
                  })
                }
                required
              />
            </label>

            <label className="block">
              <span className="rr-label">Contact Email</span>
              <input
                type="email"
                className="rr-input"
                value={form.contactEmail}
                onChange={(e) =>
                  setForm({
                    ...form,
                    contactEmail: e.target.value,
                  })
                }
                required
              />
            </label>

            <label className="block">
              <span className="rr-label">City</span>
              <input
                className="rr-input"
                value={form.city}
                onChange={(e) =>
                  setForm({ ...form, city: e.target.value })
                }
                required
              />
            </label>

            <label className="block">
              <span className="rr-label">State</span>
              <input
                className="rr-input uppercase"
                maxLength={2}
                value={form.state}
                onChange={(e) =>
                  setForm({
                    ...form,
                    state: e.target.value.toUpperCase(),
                  })
                }
                required
              />
            </label>
          </>
        )}

        {/* Password */}
        <label className="block">
          <span className="rr-label">Password</span>
          <input
            type="password"
            className="rr-input"
            value={form.password}
            onChange={(e) =>
              setForm({ ...form, password: e.target.value })
            }
            required
            minLength={8}
          />
        </label>

        {/* Confirm Password */}
        <label className="block">
          <span className="rr-label">Confirm Password</span>
          <input
            type="password"
            className="rr-input"
            value={form.confirm}
            onChange={(e) =>
              setForm({ ...form, confirm: e.target.value })
            }
            required
            minLength={8}
          />
        </label>

        {/* Alerts */}
        {err && <p className="rr-alert rr-alert-error">{err}</p>}
        {msg && <p className="rr-alert rr-alert-success">{msg}</p>}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-500 active:bg-red-700 disabled:opacity-60"
        >
          {loading ? "Creating..." : "Create Account"}
        </button>
      </form>
    </main>
  );
}